import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ThreeDModel from "@/components/ThreeDModel";
import BeforeAfter from "@/components/BeforeAfter";
import { api, mediaUrl, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Box, Image as ImageIcon, Compass, ArrowRight, Sparkles, Globe } from "lucide-react";

export default function SharePage() {
  const { id } = useParams();
  const [data, setData] = useState(undefined); // undefined=loading, null=notfound
  const [ready, setReady] = useState(false);
  const [fx, setFx] = useState(null);            // {rates, currencies:[{code,symbol}]}
  const [displayCur, setDisplayCur] = useState(null);

  useEffect(() => {
    api.get(`/public/projects/${id}`).then(({ data }) => {
      setData(data);
      setDisplayCur(data.display_currency || data.estimate_summary?.currency || "USD");
      setTimeout(() => setReady(true), 150);
    }).catch(() => setData(null));
  }, [id]);

  useEffect(() => {
    api.get("/fx").then(({ data }) => setFx(data)).catch(() => {});
  }, []);

  if (data === undefined)
    return (
      <div className="min-h-screen bp-grid">
        <Navbar />
        <div className="flex justify-center py-32"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      </div>
    );

  if (data === null)
    return (
      <div className="min-h-screen bp-grid">
        <Navbar />
        <div className="mx-auto flex max-w-md flex-col items-center px-4 py-28 text-center">
          <Compass className="h-12 w-12 text-muted-foreground" />
          <h1 className="mt-4 font-heading text-2xl font-bold">Model not found</h1>
          <p className="mt-2 text-muted-foreground">This shared model doesn't exist or was removed.</p>
          <Link to="/generate" className="mt-6"><Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">Make your own</Button></Link>
        </div>
      </div>
    );

  const s = data.spec || {};
  const sum = data.estimate_summary;
  const fp = data.model3d?.floorplan;
  const srcCur = sum?.currency || "USD";
  const targetCur = displayCur || srcCur;
  const rates = fx?.rates || {};
  const targetSym = (fx?.currencies || []).find((c) => c.code === targetCur)?.symbol || sum?.currency_symbol || "$";
  // Convert an amount from the estimate's source currency into the chosen display currency.
  const conv = (amt) => {
    if (typeof amt !== "number") return amt;
    if (targetCur === srcCur || !rates[srcCur] || !rates[targetCur]) return amt;
    return (amt / rates[srcCur]) * rates[targetCur];
  };
  const sourceUrl = data.model3d?.source_url ? mediaUrl(data.model3d.source_url) : null;
  const renderUrl = data.model3d?.render_url ? mediaUrl(data.model3d.render_url) : null;

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Shared 3D Model</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl">{data.name}</h1>
        <div className="mt-2 flex flex-wrap gap-2 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
          {s.building_type && <span className="border border-border px-2 py-0.5 text-primary">{String(s.building_type).replace(/_/g, " ")}</span>}
          {s.square_footage ? <span>{s.square_footage} sqft</span> : null}
          {s.bedrooms ? <span>{s.bedrooms} bd</span> : null}
          {s.bathrooms ? <span>{s.bathrooms} ba</span> : null}
          {s.region && <span>{s.region}</span>}
        </div>

        {/* Napkin → Home reveal */}
        {sourceUrl && renderUrl && (
          <div className="mt-8" data-testid="share-reveal">
            <div className="mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />
              <h3 className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">From sketch to build — drag to reveal</h3></div>
            <BeforeAfter before={sourceUrl} after={renderUrl} beforeLabel="The sketch" afterLabel="The build" />
          </div>
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div>
            <div className="mb-3 flex items-center gap-2"><Box className="h-4 w-4 text-primary" />
              <h3 className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Interactive 3D — drag to orbit</h3></div>
            {fp && ready ? <ThreeDModel floorplan={fp} /> : (
              <div className="flex h-[460px] items-center justify-center border border-border bg-[#050a16]" data-testid="share-3d-loading">
                {fp ? <Loader2 className="h-7 w-7 animate-spin text-primary" /> : <span className="font-mono2 text-xs uppercase text-muted-foreground">No 3D model</span>}
              </div>
            )}
          </div>
          <div>
            <div className="mb-3 flex items-center gap-2"><ImageIcon className="h-4 w-4 text-primary" />
              <h3 className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Render</h3></div>
            <div className="flex min-h-[300px] items-center justify-center overflow-hidden border border-border bg-[#050a16]">
              {data.model3d?.render_url ? (
                <img src={mediaUrl(data.model3d.render_url)} alt="render" className="h-full w-full object-cover" />
              ) : (
                <span className="p-8 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">No render available</span>
              )}
            </div>
          </div>
        </div>

        {sum && (
          <>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Estimated cost</h3>
              {fx && (
                <div className="flex items-center gap-2" data-testid="share-currency-switch">
                  <Globe className="h-4 w-4 text-primary" />
                  <Select value={targetCur} onValueChange={setDisplayCur}>
                    <SelectTrigger data-testid="share-currency-select" className="h-9 w-40 rounded-sm border-border bg-transparent font-mono2 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(fx.currencies || []).map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 border border-border sm:grid-cols-4">
              {[["Est. Total", money(conv(sum.total_cost), targetSym)], ["Materials", money(conv(sum.material_cost), targetSym)], ["Labor", money(conv(sum.labor_cost), targetSym)], [`Per ${sum.unit_label || "Sq Ft"}`, money(conv(sum.cost_per_unit ?? sum.cost_per_sqft), targetSym)]].map(([l, v], i) => (
                <div key={i} className="border-r border-border p-5 last:border-r-0">
                  <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
                  <div className="mt-1 font-mono2 text-lg font-semibold text-primary" data-testid={`share-stat-${i}`}>{v}</div>
                </div>
              ))}
            </div>
            {targetCur !== srcCur && (
              <p className="mt-2 font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="share-conversion-note">
                Converted from {srcCur} at live rates · approximate
              </p>
            )}
          </>
        )}

        <div className="mt-10 flex items-center justify-between border border-border border-l-2 border-l-primary bg-secondary/20 p-6">
          <div>
            <p className="font-heading text-xl font-bold">Want one like this?</p>
            <p className="text-muted-foreground">Turn your own blueprint into a 3D model in seconds — free to try.</p>
          </div>
          <Link to="/generate" data-testid="share-cta">
            <Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              Try Costobuild <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
